import React, {
    useState,
    useMemo,
    useCallback
} from 'react';
import PropTypes from 'prop-types';

import { useTranslate } from '@gameday/utils/hooks/useTranslate';
import useTracking from '@gameday/utils/hooks/useTracking';

import DFVPlayerAuto from './components/DFVPlayerAuto';
import ErrorBoundaryWrapper from '../ErrorFallback/ErrorBoundaryWrapper';
import {
    DynamicFieldViewWrapper,
    SkeletonWrapper,
    AccordionWrapper,
    VenueLabel
} from './DynamicFieldView.style';
import StreamError from './utils/StreamError';

const RETRY_TIME_LIMIT = 15000;
const RETRY_COUNT_LIMIT = 2;

const ACCORDIAN_ANIMATION = {
    animate: true
};

const DynamicFieldView = ({
    fieldViewStream,
    venueId,
    venueName,
    renderStandalone = true
}) => {
    const translate = useTranslate();
    const Tracking = useTracking();

    const [isLoading, setIsLoading] = useState(true);
    const [allowRetry, setAllowRetry] = useState(true);
    const [retryCount, setRetryCount] = useState(0);
    const [timeoutRan, setTimeoutRan] = useState(false);
    const [isContainerOpen, setIsContainerOpen] = useState(true);
    const [streamError, setStreamError] = useState(false);
    const [autoPlayError, setAutoPlayError] = useState(false);

    const trackContainerAction = useCallback((action) => {
        const cfg = {
            componentName: ['Live'],
            activity: `Field View ${action} Click`
        };

        Tracking.track.track_click(cfg);
    }, [Tracking]);

    const retryLimiter = useCallback(() => {
        setAllowRetry((retryCount < RETRY_COUNT_LIMIT));
        setRetryCount((prevValue) => prevValue + 1);

        if (!timeoutRan) {
            setTimeout(() => {
                setTimeoutRan(false);
                setAllowRetry(true);
                setRetryCount(0);
            }, RETRY_TIME_LIMIT);
        }

        if (allowRetry && retryCount < 3) {
            setStreamError(false);
        }

        setTimeoutRan(true);
    }, [retryCount, allowRetry]);

    const controlText = isContainerOpen ? translate('button_hide', 'Hide') : translate('button_show', 'Show');
    const DFV = (
        <ErrorBoundaryWrapper
            componentName='Dynamic field view'
            showButton
            buttonText='Retry'
            isButtonDisabled={!allowRetry}
            onReset={retryLimiter}
            marginOverride='0'
        >
            <DynamicFieldViewWrapper>
                {streamError && <StreamError />}
                <DFVPlayerAuto
                    stream={fieldViewStream.url}
                    setStreamError={setStreamError}
                    setIsLoading={setIsLoading}
                    setAutoPlayError={setAutoPlayError}
                    autoPlayError={autoPlayError}
                />
                {renderStandalone && !autoPlayError && <VenueLabel>{venueName}</VenueLabel>}
                {isLoading && <SkeletonWrapper width="100%" height="100%" borderRadius={0}/>}
            </DynamicFieldViewWrapper>
        </ErrorBoundaryWrapper>
    );

    const containerHandlers = useMemo(() => ({
        onClose: () => {
            setIsContainerOpen(false);
            trackContainerAction('Collapse');
        },
        onOpen: () => {
            setIsContainerOpen(true);
            trackContainerAction('Expand');
        }
    }), []);

    return (
        renderStandalone
            ? DFV
            : <AccordionWrapper
                headline={venueName}
                subHeaderText={controlText}
                persistSubHeader={true}
                showIcon={false}
                isOpen={isContainerOpen}
                handlers={containerHandlers}
                contentId={'venueId-' + venueId}
                animationOptions={ACCORDIAN_ANIMATION}
            >
                {DFV}
            </AccordionWrapper>
    );
};

DynamicFieldView.propTypes = {
    fieldViewStream: PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string
    }),
    renderStandalone: PropTypes.bool,
    venueId: PropTypes.string,
    venueName: PropTypes.string,
    id: PropTypes.string
};

export default DynamicFieldView;
